import { useEffect, useState } from 'react';
import { segment } from '@actions/analytics';
import { useSnackbar } from '@stores/snackbar';
import { emptyObject } from '@constants/empty';
import useTestSeriesStore from '@stores/test-series';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { saveItem, unsaveItem } from '@api/fetchers/test-series';
import {
  MODAL_TYPE,
  SAVE_SAVE_CONTENT,
  SAVE_UNSAVE_CONTENT
} from './constants';
import { getItemTypes } from './utils';

const useHandleSaveItem = ({
  uid,
  type,
  title,
  goalUid,
  initialSaveState = false
}) => {
  const [isSaved, setIsSaved] = useState(false);

  const { open: showSnackbar } = useSnackbar();
  const { setModalInfo } = useTestSeriesStore();
  const goalSegmentData = useGoalEventProps({ goalUID: goalUid });

  const { isTest, entityType, contentType } = getItemTypes(type);

  useEffect(() => {
    if (typeof initialSaveState === 'boolean') {
      setIsSaved(initialSaveState);
    }
  }, [initialSaveState]);

  const triggerEvent = (eventType) => {
    segment.track(eventType, {
      save_content_uid: uid,
      save_content_title: title,
      save_content_type: contentType,
      ...goalSegmentData
    });
  };

  const handleUnSaveItem = async () => {
    const res = await unsaveItem(uid, entityType);
    if (res?.successCode) {
      setIsSaved(false);
      showSnackbar({
        message: `${isTest ? 'Test' : 'Lesson'} removed successfully`,
        type: 'success'
      });
      setModalInfo(emptyObject);

      return triggerEvent(SAVE_UNSAVE_CONTENT);
    }
    showSnackbar({
      message: 'Something went wrong! Please try again later.',
      type: 'error'
    });
  };

  const handleSaveClick = async () => {
    if (isSaved) {
      return setModalInfo({
        type: MODAL_TYPE.REMOVE_SAVED_ITEM,
        message: `This will remove the ${
          isTest ? 'test' : 'lesson'
        } from your saved list. You can add it back from the test series page`,
        callBack: () => handleUnSaveItem({ uid, type, title })
      });
    }

    const res = await saveItem(uid, entityType);

    if (res?.successCode) {
      setIsSaved(true);
      showSnackbar({
        message: `Saved to ${isTest ? 'Tests' : 'Lessons'}`,
        type: 'success'
      });
      return triggerEvent(SAVE_SAVE_CONTENT);
    }

    showSnackbar({
      message: `Failed to save the ${
        isTest ? 'Test' : 'Lesson'
      }! Please try again.`,
      type: 'error'
    });
  };

  return {
    handleSaveClick,
    isSaved
  };
};

export default useHandleSaveItem;
